import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Package, Search, Filter, CheckCircle, XCircle, Edit, Eye,
  AlertTriangle, Clock, CheckCheck, Loader2, Undo2, Plus, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import * as categoryApi from '@/services/categoryApi';

const CategoryManagerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sections for approval
  const [sections, setSections] = useState([]);
  
  // View/Edit dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  // Approve dialog
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvalData, setApprovalData] = useState({
    priority: 'normal',
    target_section: '',
    remarks: ''
  });
  const [approving, setApproving] = useState(false);
  
  // Reject dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Edit Approved dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editPriority, setEditPriority] = useState('normal');
  const [editTargetSection, setEditTargetSection] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Revoke dialog
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  // Missing GS children reconciliation
  const [missingGs, setMissingGs] = useState(null);
  const [reconcilingGs, setReconcilingGs] = useState(false);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoryApi.getShipmentRequests({
        status: statusFilter === 'all' ? undefined : statusFilter
      });
      setRequests(data.requests || []);
    } catch (error) {
      toast.error('Failed to fetch shipment requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Auto-scan for approved SRs that have missing GS children (common root-cause:
  // pre-transactional approval flow). Runs once on mount; supervisor can also
  // manually trigger Fix All from the banner.
  const scanMissingGs = useCallback(async () => {
    try {
      const data = await categoryApi.findMissingGsChildren();
      setMissingGs(data);
    } catch (e) {
      // silent — not a critical flow
      setMissingGs(null);
    }
  }, []);

  useEffect(() => {
    scanMissingGs();
  }, [scanMissingGs]);

  const handleFixAllMissingGs = async () => {
    if (!missingGs?.requests?.length) return;
    if (!window.confirm(`Fix ${missingGs.total_missing_srs} SR(s) with ${missingGs.total_missing_children} missing child record(s)? New Greige Storage entries will be created for each missing item.`)) {
      return;
    }
    try {
      setReconcilingGs(true);
      let created = 0;
      for (const r of missingGs.requests) {
        try {
          const res = await categoryApi.reconcileGsChildren(r.request_id);
          created += res.created || 0;
        } catch (e) {
          toast.warning(`Could not fix ${r.request_id}: ${e.response?.data?.detail || e.message}`);
        }
      }
      toast.success(`Created ${created} missing child record(s)`);
      await scanMissingGs();
      await fetchRequests();
    } catch (e) {
      toast.error('Bulk fix failed');
    } finally {
      setReconcilingGs(false);
    }
  };

  // Fetch sections on mount
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const data = await categoryApi.getSections();
        setSections(data.sections || []);
      } catch (error) {
        console.error('Failed to fetch sections:', error);
      }
    };
    fetchSections();
  }, []);

  // Open view dialog
  const handleViewRequest = async (requestId) => {
    try {
      const data = await categoryApi.getShipmentRequest(requestId);
      setSelectedRequest(data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to fetch request details');
    }
  };

  // Open approve dialog
  const openApproveDialog = (request) => {
    setSelectedRequest(request);
    setApprovalData({
      priority: 'normal',
      target_section: '',
      remarks: ''
    });
    setShowApproveDialog(true);
  };

  // Approve request
  const handleApprove = async () => {
    if (!approvalData.target_section) {
      toast.warning('Please select a target section');
      return;
    }
    
    try {
      setApproving(true);
      await categoryApi.approveShipmentRequest(selectedRequest.request_id, approvalData);
      toast.success('Request approved and sent to section');
      setShowApproveDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  };

  // Open reject dialog
  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectRemarks('');
    setShowRejectDialog(true);
  };

  // Reject request
  const handleReject = async () => {
    try {
      setRejecting(true);
      await categoryApi.rejectShipmentRequest(selectedRequest.request_id, rejectRemarks);
      toast.success('Request rejected');
      setShowRejectDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setRejecting(false);
    }
  };

  // Open edit dialog (approved/in-progress/completed requests)
  const openEditDialog = async (request) => {
    try {
      const data = await categoryApi.getShipmentRequest(request.request_id);
      setSelectedRequest(data);
      setEditItems((data.items || []).map(i => ({ ...i })));
      setEditPriority(data.priority || 'normal');
      setEditTargetSection(data.target_section || '');
      setEditRemarks(data.approval_remarks || '');
      setShowEditDialog(true);
    } catch (error) {
      toast.error('Failed to load request for editing');
    }
  };

  const updateEditItem = (idx, field, value) => {
    setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const removeEditItem = (idx) => {
    setEditItems(prev => prev.filter((_, i) => i !== idx));
  };

  const addEditItem = () => {
    setEditItems(prev => [...prev, {
      barcode: '',
      design_name: '',
      color: '',
      operation_name: '',
      closing_qty: 0,
      shipment_qty: 1,
      target_fg_barcode: '',
      target_fg_name: ''
    }]);
  };

  const handleSaveEdit = async () => {
    // Validate new items
    for (const it of editItems) {
      if (!it.barcode || !it.design_name) {
        toast.warning('All items must have barcode and design name');
        return;
      }
      if (!it.shipment_qty || Number(it.shipment_qty) < 1) {
        toast.warning(`Shipment qty must be >= 1 for ${it.barcode || 'new item'}`);
        return;
      }
    }

    try {
      setSavingEdit(true);
      const payload = {
        items: editItems.map(it => ({
          barcode: it.barcode,
          design_name: it.design_name,
          color: it.color || '',
          operation_name: it.operation_name || '',
          closing_qty: Number(it.closing_qty || 0),
          shipment_qty: Number(it.shipment_qty),
          target_fg_barcode: it.target_fg_barcode || null,
          target_fg_name: it.target_fg_name || null
        })),
        priority: editPriority,
        target_section: editTargetSection,
        remarks: editRemarks
      };
      const res = await categoryApi.editApprovedShipmentRequest(selectedRequest.request_id, payload);
      toast.success(res.message || 'Request updated');
      if (Array.isArray(res.changes) && res.changes.length) {
        toast.info(`${res.changes.length} change(s) applied`);
      }
      setShowEditDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setSavingEdit(false);
    }
  };

  // Revoke
  const openRevokeDialog = (request) => {
    setSelectedRequest(request);
    setRevokeReason('');
    setShowRevokeDialog(true);
  };

  const handleRevoke = async () => {
    try {
      setRevoking(true);
      await categoryApi.revokeShipmentRequest(selectedRequest.request_id, revokeReason);
      toast.success('Approval revoked — request back to pending');
      setShowRevokeDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke approval');
    } finally {
      setRevoking(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      approved: 'bg-green-100 text-green-700 border-green-300',
      in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
      completed: 'bg-gray-100 text-gray-700 border-gray-300',
      rejected: 'bg-red-100 text-red-700 border-red-300'
    };
    return <Badge className={`${styles[status] || styles.pending} border`}>{status.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      normal: 'bg-blue-500 text-white',
      low: 'bg-gray-400 text-white'
    };
    return priority ? <Badge className={styles[priority] || styles.normal}>{priority.toUpperCase()}</Badge> : null;
  };

  const getModuleName = (code) => {
    const names = {
      greige: 'Greige Section',
      greige_storage: 'Greige Storage',
      fob: 'FOB Section',
      goods_transfer: 'Goods Transfer'
    };
    return names[code] || code;
  };

  const filteredRequests = requests.filter(r => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      r.request_id?.toLowerCase().includes(search) ||
      r.module?.toLowerCase().includes(search) ||
      r.created_by_name?.toLowerCase().includes(search)
    );
  });

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Category Manager Dashboard</h1>
              <p className="text-sm text-[#E8DCC8]">Review & Approve Shipment Requests</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-600">Pending Review</p>
                  <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600">Approved</p>
                  <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600">Completed</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.completed}</p>
                </div>
                <CheckCheck className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Requests</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{requests.length}</p>
                </div>
                <Package className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Missing GS Children Banner */}
        {missingGs && missingGs.total_missing_srs > 0 && (
          <Card className="border-amber-300 bg-amber-50 mb-6" data-testid="missing-gs-banner">
            <CardContent className="py-4">
              <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">
                      {missingGs.total_missing_srs} approved request(s) missing downstream Greige Storage entries
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {missingGs.total_missing_children} child record(s) to create. Greige Storage module can't see these requests until they're reconciled.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleFixAllMissingGs}
                  disabled={reconcilingGs}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="fix-missing-gs-btn"
                >
                  {reconcilingGs ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fixing...</>
                  ) : (
                    <>Fix All Missing</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-[#E5DFD0] mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search request ID, module, or creator..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Requests Found</h3>
              <p className="text-[#5A5F6E]">No shipment requests match the current filters</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Shipment Requests
                <Badge variant="outline" className="ml-2">{filteredRequests.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Request ID</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total Qty</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((request) => (
                      <TableRow key={request.request_id} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm font-medium">{request.request_id}</TableCell>
                        <TableCell>{getModuleName(request.module)}</TableCell>
                        <TableCell>{request.items?.length || 0}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-700 border-0">{request.total_shipment_qty}</Badge>
                        </TableCell>
                        <TableCell>{request.created_by_name}</TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                        <TableCell>{getPriorityBadge(request.priority)}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewRequest(request.request_id)}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {request.status === 'pending' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openApproveDialog(request)}
                                  className="border-green-500 text-green-600 hover:bg-green-50"
                                  title="Approve"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openRejectDialog(request)}
                                  className="border-red-500 text-red-600 hover:bg-red-50"
                                  title="Reject"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {['approved', 'in_progress', 'completed'].includes(request.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(request)}
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                title="Edit Approved Request"
                                data-testid={`edit-approved-${request.request_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {['approved', 'in_progress'].includes(request.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openRevokeDialog(request)}
                                className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                title="Revoke Approval"
                                data-testid={`revoke-${request.request_id}`}
                              >
                                <Undo2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Request Details - {selectedRequest?.request_id}</DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Module</p>
                    <p className="font-semibold">{getModuleName(selectedRequest.module)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Created By</p>
                    <p className="font-semibold">{selectedRequest.created_by_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Qty</p>
                    <p className="font-semibold">{selectedRequest.total_shipment_qty}</p>
                  </div>
                </div>
                
                {selectedRequest.remarks && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600 mb-1">Remarks</p>
                    <p className="text-sm">{selectedRequest.remarks}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-semibold mb-2">Items ({selectedRequest.items?.length || 0})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Barcode</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Color</TableHead>
                          <TableHead>Closing Qty</TableHead>
                          <TableHead>Ship Qty</TableHead>
                          <TableHead>Target FG</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRequest.items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{item.barcode}</TableCell>
                            <TableCell>{item.design_name}</TableCell>
                            <TableCell>{item.color || '-'}</TableCell>
                            <TableCell>{item.closing_qty}</TableCell>
                            <TableCell className="font-semibold">{item.shipment_qty}</TableCell>
                            <TableCell>{item.target_fg_name || item.design_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Shipment Request</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Request: <span className="font-semibold">{selectedRequest?.request_id}</span></p>
                <p className="text-sm text-gray-600">Module: <span className="font-semibold">{getModuleName(selectedRequest?.module)}</span></p>
                <p className="text-sm text-gray-600">Total Qty: <span className="font-semibold">{selectedRequest?.total_shipment_qty}</span></p>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Priority *</Label>
                <Select value={approvalData.priority} onValueChange={(v) => setApprovalData(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">🔴 Urgent</SelectItem>
                    <SelectItem value="high">🟠 High</SelectItem>
                    <SelectItem value="normal">🔵 Normal</SelectItem>
                    <SelectItem value="low">⚪ Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Target Section *</Label>
                <Select value={approvalData.target_section} onValueChange={(v) => setApprovalData(prev => ({ ...prev, target_section: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Section" />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map(s => (
                      <SelectItem key={s.section_name} value={s.section_name}>{s.section_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Remarks (Optional)</Label>
                <Textarea
                  placeholder="Add any notes for the section..."
                  value={approvalData.remarks}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleApprove} 
                disabled={approving}
                className="bg-green-600 hover:bg-green-700"
              >
                {approving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Approving...</> : 'Approve & Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Reject Request
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to reject request <span className="font-semibold">{selectedRequest?.request_id}</span>?
              </p>
              
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Reason for Rejection</Label>
                <Textarea
                  placeholder="Provide a reason for rejection..."
                  value={rejectRemarks}
                  onChange={(e) => setRejectRemarks(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleReject} 
                disabled={rejecting}
                className="bg-red-600 hover:bg-red-700"
              >
                {rejecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rejecting...</> : 'Reject Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Approved Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-approved-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" />
                Edit Approved Shipment Request — {selectedRequest?.request_id}
              </DialogTitle>
              <DialogDescription>
                Modify line items, priority, target section or revoke approval. Changes cascade to downstream pick slips where applicable.
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm">
                  <p><strong>Status:</strong> {selectedRequest.status?.replace('_', ' ').toUpperCase()}</p>
                  {selectedRequest.status === 'in_progress' && (
                    <p className="text-orange-700 mt-1">
                      ⚠ This request is IN-PROGRESS. Active pickers will be notified of changes.
                    </p>
                  )}
                  {selectedRequest.status === 'completed' && (
                    <p className="text-gray-700 mt-1">
                      Completed requests: only unfulfilled lines and new additions are allowed.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Priority</Label>
                    <Select value={editPriority} onValueChange={setEditPriority}>
                      <SelectTrigger data-testid="edit-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">🔴 Urgent</SelectItem>
                        <SelectItem value="high">🟠 High</SelectItem>
                        <SelectItem value="normal">🔵 Normal</SelectItem>
                        <SelectItem value="low">⚪ Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Target Section</Label>
                    <Select value={editTargetSection} onValueChange={setEditTargetSection}>
                      <SelectTrigger data-testid="edit-target-section-select">
                        <SelectValue placeholder="Select Section" />
                      </SelectTrigger>
                      <SelectContent>
                        {sections.map(s => (
                          <SelectItem key={s.section_name} value={s.section_name}>{s.section_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Remarks</Label>
                    <Input
                      value={editRemarks}
                      onChange={(e) => setEditRemarks(e.target.value)}
                      placeholder="Optional note..."
                      data-testid="edit-remarks-input"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Items ({editItems.length})</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addEditItem}
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      data-testid="edit-add-item-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Barcode</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Color</TableHead>
                          <TableHead>Ship Qty</TableHead>
                          <TableHead>Target FG Barcode</TableHead>
                          <TableHead>Target FG Name</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editItems.map((it, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Input
                                value={it.barcode || ''}
                                onChange={(e) => updateEditItem(idx, 'barcode', e.target.value)}
                                className="h-8 text-xs font-mono"
                                data-testid={`edit-item-barcode-${idx}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={it.design_name || ''}
                                onChange={(e) => updateEditItem(idx, 'design_name', e.target.value)}
                                className="h-8 text-xs"
                                data-testid={`edit-item-design-${idx}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={it.color || ''}
                                onChange={(e) => updateEditItem(idx, 'color', e.target.value)}
                                className="h-8 text-xs"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={it.shipment_qty || ''}
                                onChange={(e) => updateEditItem(idx, 'shipment_qty', e.target.value)}
                                className="h-8 text-xs w-24"
                                data-testid={`edit-item-qty-${idx}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={it.target_fg_barcode || ''}
                                onChange={(e) => updateEditItem(idx, 'target_fg_barcode', e.target.value)}
                                className="h-8 text-xs font-mono"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={it.target_fg_name || ''}
                                onChange={(e) => updateEditItem(idx, 'target_fg_name', e.target.value)}
                                className="h-8 text-xs"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEditItem(idx)}
                                className="text-red-500 hover:bg-red-50"
                                data-testid={`edit-item-remove-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {editItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-gray-500 py-4">
                              No items. Click "Add Item" to add.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-edit-btn"
              >
                {savingEdit ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revoke Dialog */}
        <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
          <DialogContent data-testid="revoke-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Undo2 className="w-5 h-5 text-orange-500" />
                Revoke Approval
              </DialogTitle>
              <DialogDescription>
                Send the request back to PENDING. Reserved (unpicked) bundles will be released.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                This will revert request <span className="font-mono font-semibold">{selectedRequest?.request_id}</span> back to <span className="font-semibold">PENDING</span>.
                Any reserved (unpicked) bundles will be released back to inventory.
              </p>
              <p className="text-xs text-orange-700 bg-orange-50 p-2 rounded">
                ⚠ Blocked if any bundles have already been picked downstream.
              </p>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Reason (optional)</Label>
                <Textarea
                  placeholder="Why revoke?"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  rows={2}
                  data-testid="revoke-reason-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRevokeDialog(false)}>Cancel</Button>
              <Button
                onClick={handleRevoke}
                disabled={revoking}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-revoke-btn"
              >
                {revoking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Revoking...</> : 'Revoke to Pending'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CategoryManagerDashboard;
